import { getEntityRef } from "@client/content/lib/helper";
import { makeHeadingPropsSelector, makeModuleApiInfoSelector } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { getNoImageUrl } from "@client/lib/assets";
import { connectCustom } from "@client/lib/misc";
import { getMultiselectValue } from "@client/lib/settings";
import {
  type SettingsPropsParametricSelector,
  TargetSelector,
  combinedSettingsSelector,
  getColorSelector,
  getColorWithDefaultSelector,
  getSettingValueSelector,
  makeEntitySelector,
  makeTextStyleSelector
} from "@client/selectors";
import { type AppState } from "@client/types";
import { addToCart, buyNow } from "../actions";
import { localStateSelector } from "../selectors";
import { type ProductDetail } from "../types";
import Component, { type ActionProps, type MapProps } from "./component";

const makeMapStateToProps = () => {
  const apiInfoSelector = makeModuleApiInfoSelector();
  const descriptionStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "description");
  const elementsSelector = getSettingValueSelector(TargetSelector.Entity, "elements", undefined, getMultiselectValue);
  const entitySelector = makeEntitySelector();
  const nameHeadingPropsSelector = makeHeadingPropsSelector(
    TargetSelector.Entity,
    "name"
  ) as SettingsPropsParametricSelector;

  const regularPriceStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "price.regular");
  const salePriceStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "price.sale");
  const outOfStockStyleSelector = makeTextStyleSelector(TargetSelector.Entity, "outOfStock");

  const captionsSelector = combinedSettingsSelector<{ add: string; buy: string }>(TargetSelector.Entity, [
    { name: "addToCartCaption", prop: "add" },
    { name: "buyNowCaption", prop: "buy" }
  ]);

  const regularPriceColorSelector = getColorSelector(TargetSelector.Entity, "price.regular.text.color");
  const oldPriceColorSelector = getColorWithDefaultSelector(
    TargetSelector.Entity,
    "price.sale.oldPriceColor",
    regularPriceColorSelector
  );

  return (state: AppState, props: RootContainerProps): MapProps => {
    const entity = entitySelector(state, props);
    const localState = localStateSelector(state, props);
    const captions = captionsSelector(state, props);

    return {
      ...props,
      addToCartCaption: captions.add,
      apiInfo: apiInfoSelector(state, props),
      buyNowCaption: captions.buy,
      cartUrl: localState.cartUrl,
      descriptionStyle: descriptionStyleSelector(state, props),
      elements: elementsSelector(state, props),
      hasCart: localState.hasCart,
      currencyCode: localState.currencyCode,
      isEditMode: state.isEditMode,
      nameHeading: nameHeadingPropsSelector(state, props),
      noImageUrl: getNoImageUrl(),
      oldPriceColor: oldPriceColorSelector(state, props),
      outOfStockStyle: outOfStockStyleSelector(state, props),
      product: localState.product!,
      productVariants: localState.productVariants,
      regularPriceStyle: regularPriceStyleSelector(state, props),
      salePriceStyle: salePriceStyleSelector(state, props),
      shouldDisplayTax: localState.shouldDisplayTax,
      socialIconsId: entity.childrenIds ? entity.childrenIds[0] : null,
      status: localState.status
    };
  };
};

const mapDispatchToProps = (dispatch: any, props: RootContainerProps): ActionProps => ({
  addToCart: (data: ProductDetail.View.AddToCart) => dispatch(addToCart(getEntityRef(props), data)),
  buyNow: (data: ProductDetail.View.AddToCart) => dispatch(buyNow(getEntityRef(props), data))
});

export default connectCustom(makeMapStateToProps, mapDispatchToProps)(Component);
